import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Label } from "./label";

const textareaVariants = cva(
    "flex min-h-[80px] w-full rounded-md border focus-visible:border-primary bg-transparent px-3 py-2 text-sm ring-offset-transparent placeholder:text-copy-lighter focus-visible:placeholder-copy focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
    {
        variants: {
            variant: {
                default: "border-input",
                card: "border-transparent bg-accent",
                error: "border-destructive",
                success: "border-success",
            },
            size: {
                default: "min-h-[80px] px-3 py-2",
                sm: "min-h-[60px] px-3 py-2 resize-none",
                lg: "min-h-[100px] px-4 py-3 text-lg",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface TextareaProps
    extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
        VariantProps<typeof textareaVariants> {
    label?: string;
    labelIcon?: React.ReactNode;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className, variant, size, label, labelIcon, ...props }, ref) => {
        return (
            <div className="w-full">
                {label && (
                    <Label icon={labelIcon} htmlFor={label}>
                        {label}
                    </Label>
                )}
                <textarea
                    className={cn(
                        textareaVariants({ variant, size, className })
                    )}
                    ref={ref}
                    {...props}
                />
            </div>
        );
    }
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
